import { AuthProvider, ErrorCode, i18nKey, WEBAUTHN_ORIGINATING_CANISTER, } from "@shared";
import { toDayString } from "./date";
/** The provider a sign-in method records as the one used (selectedAuthProviderStore). */
export function signInProvider(method) {
    switch (method.kind) {
        case "passkey":
            return AuthProvider.PASSKEY;
        case "auth_client":
            return method.provider === "ii" ? AuthProvider.II : AuthProvider.NFID;
        case "wallet":
            return method.token === "eth" ? AuthProvider.ETH : AuthProvider.SOL;
    }
}
/** Why an emailed sign-in link couldn't be sent, in the app's words. */
export function emailLinkFailure(kind) {
    switch (kind) {
        case "email_invalid":
            return i18nKey("loginDialog.invalidEmail");
        case "failed_to_send_email":
            return i18nKey("loginDialog.failedToSendEmail");
        default:
            return i18nKey("loginDialog.unexpectedError");
    }
}
/**
 * Where a sign-in got to once its identity loaded: signed in, signed in without an account yet,
 * or back to anonymous (no OpenChat identity for it, so it was signed out).
 */
export function signInOutcome(state) {
    switch (state.kind) {
        case "logged_in":
            return { kind: "logged_in" };
        case "registering":
            return { kind: "needs_account" };
        default:
            return { kind: "failed", reason: i18nKey("loginDialog.unexpectedError") };
    }
}
// ?auth=II&auth=EMAIL … narrows the sign-in options, by these names or AuthProvider's values
const QUERY_NAMES = {
    [AuthProvider.EMAIL]: "EMAIL",
    [AuthProvider.PASSKEY]: "PASSKEY",
    [AuthProvider.II]: "II",
    [AuthProvider.ETH]: "ETH",
    [AuthProvider.SOL]: "SOL",
    [AuthProvider.NFID]: "NFID",
};
/**
 * The sign-in options on offer. Without WebAuthn only email is left, plus a passkey (the OS's)
 * and Internet Identity in the native app. `restrictTo` (`?auth=`) narrows them, ignoring the
 * bare `?auth` of the emailed link's URL, and the method `last` signed in with comes first.
 */
export function signInOptions(restrictTo, { webAuthn, native }, last) {
    const supported = webAuthn
        ? [
            AuthProvider.EMAIL,
            AuthProvider.PASSKEY,
            AuthProvider.II,
            AuthProvider.ETH,
            AuthProvider.SOL,
            AuthProvider.NFID,
        ]
        : native
            ? [AuthProvider.EMAIL, AuthProvider.PASSKEY, AuthProvider.II]
            : [AuthProvider.EMAIL];
    const names = new Set([...restrictTo].filter((name) => name !== ""));
    const offered = names.size === 0
        ? supported
        : supported.filter((o) => names.has(o) || names.has(QUERY_NAMES[o]));
    return last !== undefined && offered.includes(last)
        ? [last, ...offered.filter((o) => o !== last)]
        : offered;
}
/**
 * Whether an identity someone signed in again with is the user's own: the one they're signed in
 * with, or any sign-in linked to their account (`linked` is only asked for when it isn't the
 * first). A user who signed up one way and linked another can use either.
 */
export async function isOwnPrincipal(principal, current, linked) {
    if (principal === current)
        return true;
    return (await linked()).includes(principal);
}
/**
 * How a principal linked to an account signs in, from the canister it originated from. NFID has
 * canisters of its own (NFID Wallet's among them), so one that's none of these is taken for NFID.
 */
export function linkedPrincipalProvider(principal, canisters) {
    switch (principal.originatingCanister) {
        case WEBAUTHN_ORIGINATING_CANISTER:
            return AuthProvider.PASSKEY;
        case canisters.ethereum:
            return AuthProvider.ETH;
        case canisters.solana:
            return AuthProvider.SOL;
        case canisters.email:
            return AuthProvider.EMAIL;
        case canisters.internetIdentity:
            return principal.isIIPrincipal ? AuthProvider.II : AuthProvider.NFID;
        default:
            return AuthProvider.NFID;
    }
}
/**
 * The sign-in methods linked to an account, to offer for signing in again, as `signInOptions`'
 * `restrictTo`. Empty, which offers them all, when `platform` has none of them: there has to be
 * something to pick.
 */
export function linkedSignInProviders(linked, platform) {
    const providers = new Set(linked.map((l) => l.provider));
    return signInOptions(providers, platform).length > 0 ? providers : new Set();
}
/**
 * Why a sign-in method couldn't be unlinked (removeIdentityLink), or undefined when it was. The
 * one the user is signed in with can't be.
 */
export function unlinkIdentityFailure(resp) {
    switch (resp) {
        case "success":
            return undefined;
        case "cannot_unlink_active_principal":
            return i18nKey("identity.failure.unlinkActivePrincipal");
        case "identity_link_not_found":
            return i18nKey("identity.failure.identityNotFound");
        case "user_not_found":
            return i18nKey("identity.failure.userNotFound");
        default:
            return resp;
    }
}
/** A linked sign-in method's "Last used 12 Oct 2026" line, from its `lastUsed` (ms). */
export function signInLastUsed(lastUsed, locale) {
    return i18nKey("identity.linkedAccounts.lastUsed", {
        date: toDayString(new Date(Number(lastUsed)), locale),
    });
}
/** A linked sign-in method's principal cut to its first and last groups, "7z6fx-...-wae". */
export function shortSignInPrincipal(principal) {
    const first = principal.indexOf("-");
    const last = principal.lastIndexOf("-");
    if (first === last)
        return principal;
    return `${principal.slice(0, first + 1)}...${principal.slice(last)}`;
}
/**
 * The linked sign-in methods with the one this session signed in with marked (isCurrentIdentity).
 * The backend marks it by the session's auth principal, which a passkey's linked principal never
 * is, so when a passkey's credential is the one this session used, that passkey is the one
 * marked, and only it.
 */
export function markCurrentSignIn(principals, currentCredentialId) {
    const matches = (p) => currentCredentialId !== undefined &&
        p.webAuthnKey !== undefined &&
        sameBytes(p.webAuthnKey.credentialId, currentCredentialId);
    if (!principals.some(matches))
        return principals;
    return principals.map((p) => ({ ...p, isCurrentIdentity: matches(p) }));
}
function sameBytes(a, b) {
    return a.length === b.length && a.every((byte, i) => byte === b[i]);
}
export function linkIdentitiesResult(resp) {
    const failed = (key, startOver = true) => ({
        kind: "failed",
        reason: i18nKey(key),
        startOver,
    });
    if (typeof resp === "object") {
        if (resp.kind === "success")
            return { kind: "linked" };
        switch (resp.code) {
            case ErrorCode.PrincipalAlreadyUsed:
                return failed("identity.failure.alreadyLinked");
            case ErrorCode.MaxLinkedIdentitiesLimitReached:
                return failed("identity.failure.limitReached");
            default:
                return failed("identity.failure.link");
        }
    }
    switch (resp) {
        case "success":
            return { kind: "linked" };
        // already linked to this account: nothing to start over
        case "already_linked_to_principal":
            return failed("identity.failure.alreadyLinked", false);
        // linked to another account
        case "already_registered":
            return failed("identity.failure.alreadyLinked");
        case "linked_identities_limit_reached":
            return failed("identity.failure.limitReached");
        case "principal_mismatch":
            return failed("identity.failure.principalMismatch");
        default:
            return failed("identity.failure.link");
    }
}
